module Sm.UI.Pad

open System
open MobF
open Fable.DateFunctions
open FsToolkit.ErrorHandling

open Sm.Core.Date

open Sm.Service.Storage
open Sm.Service.Repository

open Sm.Model
open Sm.Model.Record
open Sm.Model.RecordSet

open Sm.UI.Entry
open Sm.UI.Activity
open Sm.UI.Form
open Sm.UI.Form.DateInput
open Sm.UI.Form.Control
open Sm.UI.InputParser
open Sm.UI.Interaction

let [<Literal>] AutoTimerDuration = 3

type Step = {
    Time: Activity.StepTime
    Record: Record.Type
    IsCurrent: bool
    StartTime: string option
}

type Entry =
    | Memo of Record.Type
    | Step of Step

type Marginalia = {
    EndTime: TimeOnly
    Duration: TimeSpan
    StartTimes: Map<Guid, TimeOnly>
}

type Msg =
    | Noop
    | ShowToday
    | ShowDate of DateOnly
    | Tick
    | ScrollToBottom
    | Add of Input
    | SelectDate of DateOnly
    | ShowCalendar
    | HideCalendar
    | IncrementTimer

type ActiveDate =
    | Auto of DateOnly
    | Manual of DateOnly

type State = {
    Date: ActiveDate
    Time: TimeOnly
    Interaction: Interaction
    Input: TextInput.Type
    ScrollSignal: int
    ExpandedPages: Set<DateOnly>
    LastTimestamp: Timestamp option
    DateInput: DateInput.Type<Msg> option
}

type Computed =
    inherit Timer.StepSource

    abstract ActiveDate: DateOnly
    abstract IsToday: bool
    abstract PastDates: DateOnly list
    abstract ActivityOn: Set<DateOnly>
    abstract Records: Record.Type list
    abstract Marginalia: Marginalia
    abstract Entries: Entry list
    abstract PendingSteps: Step list
    abstract PendingMemos: Record.Type list
    abstract Estimate: string option
    abstract InputTokens: SyntaxNode list
    abstract ShowMemoInput: bool
    abstract DateDisplay: {| Text: string; IsToday: bool |}
    abstract ShowPreviousDate: Button<Msg>
    abstract ShowNextDate: Button<Msg>
    abstract ShowToday: Button<Msg>
    abstract ShowCalendar: Button<Msg>

type Pad = Model<State, Msg, Computed>

let create interaction (repository: Repository) : Pad =
    let currentDateAndTime () =
        let now = DateTime.Now
        DateOnly.FromDateTime(now), TimeOnly.FromDateTime(now)

    let pastQuery =
        let query =
            RealtimeQuery.create {
                Empty = Set.empty
                Repository = repository
                Execute = (fun () -> repository.QueryActivity(JournalCode))
                Merge = (fun _ e set ->
                    match Id.parse e.Id with
                    | Some (_, timestamp) ->
                        if set.Contains(timestamp.Date) then set
                        else set.Add(timestamp.Date)
                    | _ -> set
                )
            }
        query.Post(RealtimeQuery.Execute ())
        query

    let entryRecords = RecordSet.create repository Options.Default
    
    let pendingMemos =
        let query = RecordQuery.Pending
        let rs = RecordSet.create repository Options.Default
        rs.Post(Load query)
        rs

    let loadDate date =
        let query = Journal date
        entryRecords.Post(Load query)

    let init post =
        entryRecords |> Subscribe.auto
            (fun x -> x.Version)
            (function
                | 0 -> ()
                | _ -> post ScrollToBottom
            )

        let date, time = currentDateAndTime ()
        loadDate date

        {
            Date = Auto date
            Time = time
            Interaction = interaction
            Input =
                TextInput.create {
                    TextInput.Config.Default with
                        Accept = InputEditor.accept (Add >> post)
                        Validate = InputEditor.validate
                        Multiline = true
                        Autoexpand = true
                }
            ScrollSignal = 0
            ExpandedPages = Set.empty
            LastTimestamp = None
            DateInput = None
        }

    let update (state, post, computed: Computed) = function
        | Noop -> state

        | ShowToday ->
            post (ShowDate DateOnly.Today)
            state

        | ShowDate _ when interaction.State.Locked ->
            state

        | ShowDate date ->
            interaction.Post(EndInput)
            loadDate date
            
            if date = DateOnly.Today then
                { state with State.Date = Auto DateOnly.Today }
            else
                { state with State.Date = Manual date }

        | Tick ->
            let _, time = currentDateAndTime ()

            match state.Date with
            | Auto d when not <| d.IsToday -> post ShowToday
            | _ -> ()

            { state with Time = time }

        | ScrollToBottom ->
            { state with ScrollSignal = state.ScrollSignal + 1 }

        | Add input ->
            let metadata =
                Meta.collectOn input.Metadata [
                    if computed.ShowMemoInput then SetMemo
                ]

            //Ensure that timestamps always increase when adding entries to the
            //journal in quick succession (the sequencing is based entirely on
            //timestamps for the moment)
            let timestamp =
                let now = Timestamp.now ()

                match state.LastTimestamp with 
                | None -> now
                | Some t -> max t now
                |> fun t -> { t with Time = t.Time.Add(TimeSpan.OneSecond) }

            let source =
                {
                    Id = Id.create JournalCode timestamp (Guid.NewGuid())
                    Text = input.Text
                    Metadata = metadata
                }

            entryRecords.Post(Insert source)

            { state with ScrollSignal = state.ScrollSignal + 1; LastTimestamp = Some timestamp }

        | IncrementTimer ->
            let est =
                computed.InputTokens
                |> List.tryPick (function
                    | Timer t -> Some (t + 1)
                    | _ -> None
                )
                |> Option.defaultValue AutoTimerDuration

            let text =
                computed.InputTokens
                |> List.rev
                |> function
                    | Timer _ :: rest -> Timer est :: rest
                    | tokens -> Timer est :: Text " " :: tokens
                |> List.rev
                |> format

            state.Input.Post(TextInput.Edit text)
            post ScrollToBottom

            state

        | SelectDate _ when interaction.State.Locked ->
            state

        | SelectDate date ->
            post (ShowDate date)
            post HideCalendar
            state

        | ShowCalendar when interaction.State.Locked ->
            state

        | ShowCalendar ->
            interaction.Post(EndInput)
            let today = DateOnly.Today

            let config = {
                InitialValue = Some computed.ActiveDate
                AcceptMessage = SelectDate
                MapAvailability = (fun date ->
                    if date > today then Unavailable
                    elif computed.ActivityOn.Contains(date) then Available
                    else Unavailable
                )
                PermitMonth = fun date -> date < today
            }

            let input = DateInput.create config
            { state with DateInput = Some input }

        | HideCalendar ->
            { state with DateInput = None }


    let compute (state: State) =
        let (|ActiveDay|_|) (computed: Computed) = function
            | d when d = computed.ActiveDate -> Some ()
            | _ -> None

        { new Computed with
            member this.TimerSteps =
                this.PendingSteps
                |> Seq.map (fun { Time = t; Record = r } -> t, r)
                |> Seq.toList

            member _.PastDates =
                pastQuery.Computed.Result
                |> Seq.rev
                |> List.ofSeq

            member this.ActivityOn =
                seq {
                    yield DateOnly.Today
                    yield! this.PastDates
                }
                |> Set

            member _.ActiveDate =
                match state.Date with
                | Auto d -> d
                | Manual d -> d

            member this.IsToday =
                this.ActiveDate = DateOnly.Today

            member _.Records =
                entryRecords.Computed.Records
                |> Seq.sortBy _.State.Timestamp
                |> Seq.toList

            member this.Marginalia =
                let now = state.Time
                let items =
                    this.Records 
                    |> Seq.filter _.Computed.Activity.Time.IsSome

                let times, (endTime, duration) =
                    ((now, TimeSpan.Zero), items)
                    ||> Seq.mapFold (fun (startTime, duration) item ->
                        match item.Computed.Activity.Time with
                        | Some t when t.Pending ->
                            let line = Some (item.State.Id, startTime)
                            let endTime = startTime.Add(t.Estimate)
                            let duration = duration.Add(t.Estimate)
                            line, (endTime, duration)
                        | _ ->
                            None, (startTime, duration)
                    )

                {
                    EndTime = endTime
                    Duration = duration
                    StartTimes =
                        times
                        |> Seq.choose id
                        |> Map.ofSeq
                }

            member this.Entries =
                let mutable isCurrent = true

                this.Records
                |> Seq.map (fun record ->
                    match record.Computed.Activity.Time with
                    | None ->  Memo record
                    | Some time ->
                        let entry = {
                            Time = time
                            Record = record
                            IsCurrent =
                                if time.Status = Pending && isCurrent then
                                    isCurrent <- false
                                    true
                                else false
                            StartTime =
                                this.Marginalia.StartTimes
                                |> Map.tryFind record.State.Id
                                |> Option.map (fun t -> sprintf "%02i:%02i" t.Hour t.Minute)
                        }
                        Step entry
                )
                |> Seq.toList

            member this.PendingSteps =
                this.Entries
                |> Seq.choose (function
                    | Step ({ Time = time } as s) when time.Pending -> Some s
                    | _ -> None
                )
                |> Seq.toList

            member _.PendingMemos =
                pendingMemos.Computed.Records
                |> Seq.filter _.Computed.Entry.Memo
                |> Seq.filter _.Computed.ArchiveDate.IsNone
                |> Seq.sortByDescending _.State.Timestamp
                |> List.ofSeq

            member this.Estimate =
                let m = this.Marginalia
                if m.Duration <> TimeSpan.Zero then
                    let duration = m.Duration.DurationFormat()
                    Some (sprintf "%02i:%02i (%s total)" m.EndTime.Hour m.EndTime.Minute duration)
                else 
                    None

            member _.InputTokens =
                state.Input.Computed.Value
                |> Option.map parse
                |> Option.defaultValue []

            member this.ShowMemoInput =
                match this.InputTokens |> List.rev with
                | [] -> false
                | Timer _ :: _ -> false
                | _ -> true

            member this.DateDisplay =
                let date = this.ActiveDate
                {|
                    Text = date.DateTime.Format("EEE dd LLL")
                    IsToday = date = DateOnly.Today
                |}

            member _.ShowCalendar =
                {
                    Content = Icon Calendar
                    Command =
                        if state.DateInput.IsSome then HideCalendar
                        else ShowCalendar
                    State =
                        { ButtonState.Default with
                            Classes = ["tool"]
                            Disabled = interaction.State.Locked
                            Down = state.DateInput.IsSome
                        }
                }

            member this.ShowPreviousDate =
                let today = DateOnly.Today
                let previousDay =
                    if this.ActiveDate = today then
                        //A bit awkward; if there are no entries today then the
                        //head will be yesterday which is the result we want,
                        //but if there is activity today then we need to go to
                        //the second item. This is all special cased outside of
                        //the loop - today is always a valid navigation, whether
                        //it has entries or not.
                        option {
                            let! a = List.tryHead this.PastDates
                            if a = today then return! List.tryItem 1 this.PastDates
                            else return a
                        }
                    else
                        let rec loop = function
                            | [] -> None
                            | ActiveDay this :: x :: _ -> Some x
                            | _ :: rest -> loop rest
                        this.PastDates
                        |> loop

                {
                    Content = Icon ArrowLeft
                    Command = 
                        match previousDay with
                        | None -> Noop
                        | Some d -> ShowDate d
                    State = 
                        { ButtonState.Default with
                            Classes = ["tool"]
                            Disabled = interaction.State.Locked || previousDay.IsNone 
                        }
                }

            member this.ShowNextDate =
                let nextDay =
                    let rec loop = function
                        | [] ->
                            let today = DateOnly.Today
                            if this.ActiveDate = today then None else Some today
                        | x :: ActiveDay this :: _ -> Some x
                        | _ :: rest -> loop rest
                    this.PastDates
                    |> loop

                {
                    Content = Icon ArrowRight
                    Command = 
                        match nextDay with
                        | None -> Noop
                        | Some d -> ShowDate d
                    State = 
                        { ButtonState.Default with
                            Classes = ["tool"]
                            Disabled = interaction.State.Locked || nextDay.IsNone 
                        }
                }

            member this.ShowToday =
                let isToday = this.ActiveDate.IsToday
                {
                    Content = Label "Today"
                    Command = 
                        if isToday then Noop
                        else ShowToday
                    State = 
                        { ButtonState.Default with
                            Classes = ["tool"]
                            Disabled = interaction.State.Locked || isToday
                        }
                }
        }

    Model.useInitWithPost init
    |> Model.andUpdateWithComputed update
    |> Model.createWithComputed compute
